/* global rwgps */
/** ********* IMPORTANT ***************
 * To use this component, you MUST be *
 * on a ridewithgps.com page with the *
 * following modules loaded:          *
 * app/assets/javascripts/base.js     *
 * app/assets/javascripts/js_maps.js  *
 * google_map_script_tag              *
 # webpack_manifest_script            *
 * babel_polyfill_script              *
 * webpack_bundle_tag 'common'        *
 * webpack_bundle_tag 'map_and_graph' *
 *************************************/
import React from 'react'
import {findDOMNode} from 'react-dom'
import _ from 'lodash'
import {getConsole} from 'lib/getConsole'
import {MapTypeContext} from 'common/Map/MapTypeContext'
import {RouteLine} from 'common/Map/RouteLine'
import {RenderBigMenuItem} from 'common/GlobalNav/AdminMenu/Items'
// import cx from 'classnames'

import styles from './GoogleMap.scss'

const console = getConsole('map', 'GoogleMap')

// Strip POIs here, since they get handled at the <Map /> layer now
// and we don't want the rwgps js_maps code to render old POIs
const tripToLegacyRoute = r => new rwgps.Route({
  track_points: r.rawPoints,
  points_of_interest: [],
  course_points: r.coursePoints
})

const tripToLegacyRouteModel = r => new rwgps.models.Route({
  track_points: r.rawPoints,
  points_of_interest: [],
  course_points: r.coursePoints
})

const googleFormatBounds = bounds => ({
  east: bounds[1].lng,
  north: bounds[1].lat,
  south: bounds[0].lat,
  west: bounds[0].lng
})

// nasty semiglobals
let GMAPS_LOAD_PENDING = false
let GMAPS_LOAD_CALLBACKS = []

export class GoogleMap extends React.Component {
  static defaultProps = {
    defaultOverlay: 'rwgps',
    showOverlayPicker: true
  }

  constructor (props) {
    super(props)
    this.state = {
      initialized: false,
      mapContext: {mapType: 'google', mapInstance: null}
    }

    this._mapId = Math.random().toString(36).substr(7)
    this._canvasId = Math.random().toString(36).substr(7)
    this._profileId = Math.random().toString(36).substr(7)

    if (props.highlightSlice && props.routes.length > 1) {
      throw TypeError("Can't highlight a dataslice when more than one route is being shown.")
    }
    this.throttledResize = _.throttle(this.resizeSampleGraph, 100)
  }

  componentDidMount () {
    const gmapsLoaded = window.google && window.google.maps

    if (gmapsLoaded) {
      this.initMap()
    } else if (GMAPS_LOAD_PENDING) {
      GMAPS_LOAD_CALLBACKS.push(this.initMap)
    } else {
      this.lazyLoadGoogle()
    }
  }

  componentWillUnmount () {
    // right now, there's no way to prevent the google maps event handlers from
    // leaking, which means that we may have memory issues if the user creates
    // and deletes a LOT of maps for a report. The official gmaps stance on this
    // is 'just reuse the existing map div', but the design of react makes this
    // difficult and it would require us to do some pretty unfortunate singleton
    // /DOM-mutation stuff in this component. (see http://goo.gl/UWsnR0 for more
    // info) For now, the hanging event handler doesn't actually cause errors,
    // so we won't worry about it.
    window.removeEventListener('resize', this.throttledResize)
    if (this._sampleGraph) {
      this._sampleGraph.unbindFrom(this._sampleGraph, 'segmentselected', this.onSegmentSelected)
      this._sampleGraph.unbindFrom(this._sampleGraph, 'pointselected', this.onSegmentUnselected)
    }
    rwgps.Events.unbind('map:highlight_point', this.onMapHighlightPoint)
    rwgps.Events.unbind('map:unhighlight_nearest_point', this.onMapUnhighlightPoint)
    window.google.maps.event.removeListener(this._mapClickListenerHandle)
    window.google.maps.event.removeListener(this._mapBoundsChangedListenerHandle)
    this._mapBoundsChangedListenerHandle = null
  }

  componentDidUpdate (prevProps) {
    this.clobberMetricPref()
    this.renderMap()

    if (this.props.suggestedBounds !== prevProps.suggestedBounds) {
      if (this.props.suggestedBounds) {
        this.useSuggestedBounds()
      } else {
        this._map.callWhenReady(() => {
          this._map.gmap.fitBounds(googleFormatBounds(this.props.routes[0].boundingBox))
        })
      }
    }
  }

  useSuggestedBounds = () => {
    if (this.props.suggestedBounds) {
      this._map.callWhenReady(() => {
        // provide 150px of padding for the SegmentPopup
        this._map.gmap.fitBounds(googleFormatBounds(this.props.suggestedBounds), this.props.routes.length > 0 && {top: 150, left: 150, right: 150})
      })
    }
  }

  initMap = () => {
    const {overlay, defaultOverlay, showOverlayPicker, scrollWheelZoom, showNativeFullscreen} = this.props

    this.clobberMetricPref()
    /* we're getting close to the point where we can stop using rwpgs.Map and
       just directly use google.maps.Map */
    this._map = new rwgps.Map({
      targetDiv: this._mapId,
      overlay: (overlay || defaultOverlay),
      onOverlayChanged: this.onOverlayChanged,
      showOverlayPicker: showOverlayPicker,
      fullscreenControl: showNativeFullscreen,
      noCustomControls: true,
      mode: 'view',
      scrollwheel: !!scrollWheelZoom,
      eleProfileId: this._profileId
    })

    this._bikeLayer = new window.google.maps.BicyclingLayer()

    if (this.props.sampleGraph) {
      this._sampleGraph = new window.SampleGraph({canvasId: this._canvasId, displayControls: false})
      const sg = this._sampleGraph
      sg.bindTo(sg, 'segmentselected', this.onSegmentSelected)
      sg.bindTo(sg, 'pointselected', this.onSegmentUnselected)
      sg.removeMapBindings()
    } else {
      // If GoogleMap isn't rendering the sample graph, it may be rendered somewhere else, so we need
      // to hand up the fact that points are or aren't highlighted.
      // Doing this when props.sampleGraph is true results in the nearest point marker never showing up.
      // We'll resolve this once we ditch map.js
      rwgps.Events.bind('map:highlight_point', this.onMapHighlightPoint)
      rwgps.Events.bind('map:unhighlight_nearest_point', this.onMapUnhighlightPoint)
    }

    this.renderMap()
    this.useSuggestedBounds()
    if (!showOverlayPicker) {
      this.clobberDefaultControls()
    }
    window.addEventListener('resize', this.throttledResize)
    console.log('initialized')
    this.setState({
      initialized: true,
      mapContext: {mapType: 'google', mapInstance: this._map}
    })
  }

  onMapHighlightPoint = (pointsAround) => {
    this.props.onPointHighlighted({index: pointsAround.closestI, routeId: this._activeRouteId})
  }

  onMapUnhighlightPoint = () => {
    this.props.onPointHighlighted(null)
  }

  lazyLoadGoogle () {
    GMAPS_LOAD_PENDING = true
    console.log('Starting Google Maps API lazy load')
    const script = document.createElement('script')
    const apiKey = rwgps.config.gmapsApiKey
    const version = '3.31'
    const mapLanguage = rwgps.config.gmapsMapLanguage
    const mapRegion = ''
    const src = '//maps.google.com/maps/api/js' +
      '?' + apiKey +
      '&v=' + version +
      '&language=' + mapLanguage +
      '&region=' + mapRegion
    window.onLazyLoadGoogleComplete = () => {
      console.log('Google Maps lazy load completed, running callbacks')
      // clean up
      delete window.onLazyLoadGoogleComplete
      GMAPS_LOAD_PENDING = false
      GMAPS_LOAD_CALLBACKS.forEach(cb => cb())
      GMAPS_LOAD_CALLBACKS = []
      this.onLazyLoadGoogleComplete()
    }
    script.setAttribute('src', src)
    script.setAttribute('onload', 'window.onLazyLoadGoogleComplete()')
    document.body.appendChild(script)
  }

  onLazyLoadGoogleComplete = () => {
    // bail out if unmounted
    if (!findDOMNode(this)) {
      return
    }
    // BOOM WORKING EXCEPT FOR UnauthorizedURLForClientIdMapError
    this.setState({gmapsLoaded: true}, () => this.initMap())
  }

  onOverlayChanged = (overlay) => {
    // onOverlayChangedd gets called during map initialization with no overlay
    if (!overlay) { return }
    const {onOverlayChanged} = this.props
    onOverlayChanged && onOverlayChanged(overlay.opts.id)
  }

  onClick = (e) => {
    const {onMapClick} = this.props
    // todo - normalize this event!
    onMapClick && onMapClick(e)
  }

  // This is an instance where using ImmutableJS would make
  // shallow comparison a feasible shouldComponentUpdate approach.
  // Instead, we have to check the array elements with a deep compare
  shouldComponentUpdate (nextProps, nextState) {
    const {routes, children, sampleGraph, overlay, highlightSlice, highlightPoint} = this.props
    const {initialized} = this.state
    if (sampleGraph !== nextProps.sampleGraph) { return true }
    if (children !== nextProps.children) { return true }
    if (highlightPoint !== nextProps.highlightPoint) { return true }
    if (!_.isEqual(initialized, nextState.initialized)) { return true }
    if (!_.isEqual(highlightSlice, nextProps.highlightSlice)) { return true }
    if (!_.isEqual(routes.map(r => r.id), nextProps.routes.map(r => r.id))) { return true }
    if (overlay !== nextProps.overlay) { return true }
    return false
  }

  // this is not the greatest, but because some of the utility methods
  // of SampleGraph/Map don't have access to the config object it's not a
  // straightforward refector to move this option to config on the Map/SG
  // TODO: revisit this the next time we refactor Map and/or SG.
  clobberMetricPref = () => {
    try {
      rwgps.LabelMaker.metric = !!this.props.metricUnits
    } catch (probablyTypeError) {
      rwgps.LabelMaker = {metric: !!this.props.metricUnits}
    }
  }

  clobberDefaultControls = () => {
    // use an invisible div to shove the 'bike paths' control 120px to the right
    const placeholder = document.createElement('div')
    placeholder.className = styles.selectPlaceholder
    placeholder.index = -1 // so gross
    this._map.gmap.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(placeholder)
  }

  renderMap = () => {
    const {routes, sampleGraph, highlightSlice, highlightPoint, scrollWheelZoom} = this.props
    const {initialized} = this.state
    if (!initialized) { return }

    const map = this._map

    if (!routes) { return }

    const handleHighlight = () => {
      const prevActiveMap = window.Routes.activeMap
      window.Routes.activeMap = map

      if (highlightSlice) {
        rwgps.highlightPoints(highlightSlice[0], highlightSlice[1])
      } else {
        rwgps.unhighlightPoints()
      }

      if (highlightPoint) {
        rwgps.highlightPoint(highlightPoint.index)
      } else {
        map.unhighlightNearestPoint()
      }

      window.Routes.activeMap = prevActiveMap
    }

    map.callWhenReady(() => {
      if (routes.length > 1) {
        this._singleRoute = null
        const models = routes.map(tripToLegacyRouteModel)
        map.showAllRoutes(models)
        this._activeRouteId = null
      } else if (routes.length === 1 && routes[0].id !== this._activeRouteId) {
        this._singleRoute = tripToLegacyRoute(routes[0])
        map.clearMap()
        map.loadRoute(this._singleRoute, true)
        map.setActiveRoute(this._singleRoute)
        window.google.maps.event.addListenerOnce(map.gmap, 'idle', handleHighlight)
        // loadRouteForView clobbers registered click events, so we need to
        // re-add them here
        this._mapClickListenerHandle = map.gmap.addListener('click', this.onClick)
        this._activeRouteId = routes[0].id
        if (this.props.lastCenter && this.props.lastZoom) {
          this._map.gmap.setCenter(this.props.lastCenter)
          this._map.gmap.setZoom(this.props.lastZoom)
        } else {
          this.props.suggestedBounds
            ? this._map.gmap.fitBounds(googleFormatBounds(this.props.suggestedBounds), this.props.routes.length > 0 && {top: 150, left: 150, right: 150})
            : this._map.gmap.fitBounds(this._singleRoute.gBounds)
        }
      } else {
        handleHighlight()
      }

      if (sampleGraph) {
        window.google.maps.event.trigger(map.gmap, 'resize')
        this.renderSampleGraph()
      }
      if (!this.props.showOverlayPicker) {
        // if we're not using the native overlay picker, set overlay on render
        this._map.gmap.setMapTypeId(this.props.overlay)

        if (this.props.showBikePaths) {
          this._bikeLayer.setMap(this._map.gmap)
        } else {
          this._bikeLayer.setMap(null)
        }
      }
      if (!this._mapBoundsChangedListenerHandle) {
        this._mapBoundsChangedListenerHandle = map.gmap.addListener('bounds_changed', this.onBoundsChanged)
      }

      map.gmap.set('scrollwheel', scrollWheelZoom)
    }) // map.callWhenReady
  }

  onBoundsChanged = () => {
    if (this.props.onZoomCenterChanged) {
      const center = this._map.gmap.getCenter()
      this.props.onZoomCenterChanged(this._map.gmap.getZoom(), {lat: center.lat(), lng: center.lng()})
    }
  }

  renderSampleGraph () {
    const {highlightSlice} = this.props

    if (this.showSampleGraph()) {
      const points = this._map.activeRoute.trackPoints()
      this._sampleGraph.data(points)
      this._sampleGraph.metricsEnabled(['ele'])
      this._sampleGraph.render()
      this.resizeSampleGraph()

      if (highlightSlice) {
        const sliceWidth = highlightSlice[1] - highlightSlice[0]
        const dataSlice = this._sampleGraph.dataSlice(highlightSlice[0], 1 + sliceWidth)
        this._sampleGraph.zoomToDataSlice(dataSlice)
      }
    }
  }

  resizeSampleGraph = () => {
    if (!this._sampleGraph || !this.showSampleGraph()) {
      return
    }
    this._sampleGraph.resize(this.refs.elevationProfile.clientWidth, 150)
  }

  showSampleGraph () {
    const {sampleGraph, routes} = this.props
    return sampleGraph && routes && routes.length === 1
  }

  onSegmentSelected = (segment) => {
    this.props.onSegmentSelected &&
      this.props.onSegmentSelected([segment.i, segment.i + segment.count])
  }

  onSegmentUnselected = () => {
    this.props.onSegmentUnselected &&
      this.props.onSegmentUnselected()
  }

  onMouseOver = () => {
    // sometimes onMouseOut doesn't fire properly, so we make extra sure here
    window.Routes.sampleGraph && window.Routes.sampleGraph.removeMapBindings()
    this._preservedMapRefs = {
      sampleGraph: window.Routes.sampleGraph,
      activeMap: window.Routes.activeMap
    }
    window.Routes.sampleGraph = this._sampleGraph
    window.Routes.activeMap = this._map
    this._sampleGraph && this._sampleGraph.initializeMapBindings()
  }

  onMouseOut = () => {
    if (this._sampleGraph) {
      this._sampleGraph.removeMapBindings()
    }
    window.Routes.sampleGraph = this._preservedMapRefs.sampleGraph
    window.Routes.activeMap = this._preservedMapRefs.activeMap
    this._preservedMapRefs = null
  }

  render () {
    const {width, height, graphClassName, children, routes, showDistanceMarkers} = this.props
    const {initialized, mapContext} = this.state
    const showSg = this.showSampleGraph()
    const sgDisplay = showSg ? 'block' : 'none'

    return (
      <div style={{height, width}}
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}>
        {initialized && this._map && // seperating this from the MapTypeContext provider so an error here doesn't tank the map
          <RenderBigMenuItem getEast={() => this._map.gmap.getBounds().getNorthEast().lng()}
            getWest={() => this._map.gmap.getBounds().getSouthWest().lng()}
            getNorth={() => this._map.gmap.getBounds().getNorthEast().lat()}
            getSouth={() => this._map.gmap.getBounds().getSouthWest().lat()} />}
        <div style={{height: showSg ? `calc(${height} - 150px)` : height}} className={styles.mapContainer} id={this._mapId}>
          &nbsp;
          {initialized && this._map &&
            <MapTypeContext.Provider value={mapContext}>
              {/* Render an invisible RouteLine to display distance markers. This also lays the groundwork for switching to just using the raw google.maps API for rendering soon... */}
              {showDistanceMarkers &&
                routes &&
                routes.length === 1 &&
                <RouteLine showDistanceMarkers color='transparent' showStartMarker={false} showEndMarker={false} route={routes[0]} />}
              {children}
            </MapTypeContext.Provider>}
        </div>
        <div id={this._profileId} ref='elevationProfile' className={graphClassName} style={{height: 150, display: sgDisplay}}>
          <canvas id={this._canvasId} />
        </div>
      </div>
    )
  }
}
